import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from '../../rocks/api';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

export const mastraAssignmentKeys = {
  all: ['mastra-assignments'] as const,
  agents: () => [...mastraAssignmentKeys.all, 'agents'] as const,
  workflows: () => [...mastraAssignmentKeys.all, 'workflows'] as const,
};

export function useAgentTeamAssignments() {
  return useQuery({
    queryKey: mastraAssignmentKeys.agents(),
    queryFn: () => api.getMastraAssignments({ entity_type: 'agent' }),
  });
}

export function useWorkflowTeamAssignments() {
  return useQuery({
    queryKey: mastraAssignmentKeys.workflows(),
    queryFn: () => api.getMastraAssignments({ entity_type: 'workflow' }),
  });
}

export function useUpsertMastraAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId, teamId }: { entityType: string; entityId: string; teamId: string }) =>
      api.upsertMastraAssignment(entityType, entityId, teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mastraAssignmentKeys.all });
      toast.success('Team assignment updated');
    },
    onError: (error) => {
      toast.error('Failed to update team assignment', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteMastraAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entityType, entityId }: { entityType: string; entityId: string }) =>
      api.deleteMastraAssignment(entityType, entityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mastraAssignmentKeys.all });
      toast.success('Team assignment removed');
    },
    onError: (error) => {
      toast.error('Failed to remove team assignment', { description: getErrorMessage(error) });
    },
  });
}
